import React, { useMemo } from 'react';

const MAP_API_BASE_URL = 'https://api.plattis.org';
const TILE_BASE_URL = `${MAP_API_BASE_URL}/raster`;

const valueOf = (meta, key) => meta?.get?.(key) ?? meta?.[key];

const formatNumber = (value, digits = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return value || null;
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
};

const gpsTile = (lat, lon, zoom) => {
  const n = 2 ** zoom;
  const x = ((lon + 180) / 360) * n;
  const latRad = (lat * Math.PI) / 180;
  const y = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n;
  return { x, y };
};

const GpsMap = ({ latitude, longitude }) => {
  const zoom = 15;
  const { x, y } = gpsTile(latitude, longitude, zoom);
  const baseX = Math.floor(x);
  const baseY = Math.floor(y);
  const offsetX = (x - baseX) * 256;
  const offsetY = (y - baseY) * 256;

  const tiles = [];
  for (let dy = -1; dy <= 1; dy += 1) {
    for (let dx = -1; dx <= 1; dx += 1) {
      tiles.push({ x: baseX + dx, y: baseY + dy, dx, dy });
    }
  }

  return (
    <div className='paxsudos-metadata-map' aria-label={`Kuvauspaikka ${latitude}, ${longitude}`}>
      <div
        className='paxsudos-metadata-map__tiles'
        style={{
          left: `calc(50% - ${256 + offsetX}px)`,
          top: `calc(50% - ${256 + offsetY}px)`,
        }}
      >
        {tiles.map((tile) => (
          <img
            key={`${tile.x}-${tile.y}`}
            src={`${TILE_BASE_URL}/${zoom}/${tile.x}/${tile.y}.png`}
            alt=''
            loading='lazy'
            style={{ left: `${(tile.dx + 1) * 256}px`, top: `${(tile.dy + 1) * 256}px` }}
          />
        ))}
      </div>
      <div className='paxsudos-metadata-map__marker' title='Kuvan GPS-sijainti'>●</div>
      <div className='paxsudos-metadata-map__attribution'>© OpenStreetMap contributors</div>
    </div>
  );
};

const MetadataRow = ({ label, children }) => {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div className='paxsudos-metadata-row'>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
};

const MediaMetadataModal = ({ attachment, onClose }) => {
  const exif = attachment?.getIn?.(['meta', 'paxsudos_exif']);
  const latitude = Number(valueOf(exif, 'latitude'));
  const longitude = Number(valueOf(exif, 'longitude'));
  const hasGps = Number.isFinite(latitude) && Number.isFinite(longitude);

  const camera = [valueOf(exif, 'make'), valueOf(exif, 'model')].filter(Boolean).join(' ');
  const aperture = valueOf(exif, 'aperture');
  const focalLength = valueOf(exif, 'focal_length');
  const exposure = valueOf(exif, 'exposure_time');
  const iso = valueOf(exif, 'iso');
  const capturedAt = valueOf(exif, 'captured_at');
  const width = attachment?.getIn?.(['meta', 'original', 'width']);
  const height = attachment?.getIn?.(['meta', 'original', 'height']);

  const settings = useMemo(() => [
    focalLength ? `${focalLength} mm` : null,
    aperture ? `f/${formatNumber(aperture)}` : null,
    exposure ? `${exposure} s` : null,
    iso ? `ISO ${iso}` : null,
  ].filter(Boolean).join(' · '), [aperture, exposure, focalLength, iso]);

  return (
    <div className='paxsudos-metadata-modal'>
      <header className='paxsudos-metadata-modal__header'>
        <div>
          <strong>Kuvan metadata</strong>
        </div>
        <button type='button' onClick={onClose} aria-label='Sulje'>×</button>
      </header>

      <div className='paxsudos-metadata-modal__body'>
        <section>
          <h3>Kamera</h3>
          <dl>
            <MetadataRow label='Kamera'>{camera}</MetadataRow>
            <MetadataRow label='Objektiivi'>{valueOf(exif, 'lens')}</MetadataRow>
            <MetadataRow label='Kuvausasetukset'>{settings}</MetadataRow>
            <MetadataRow label='Kuvattu'>{capturedAt}</MetadataRow>
            <MetadataRow label='Resoluutio'>{width && height ? `${width} × ${height}` : null}</MetadataRow>
          </dl>
        </section>

        {hasGps && (
          <section>
            <h3>Sijainti</h3>
            <dl>
              <MetadataRow label='GPS'>{`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`}</MetadataRow>
            </dl>
            <GpsMap latitude={latitude} longitude={longitude} />
            <a
              className='paxsudos-metadata-modal__map-link'
              href={`/media-map?media=${encodeURIComponent(attachment?.get?.('id') ?? attachment?.id ?? '')}`}
            >
              Avaa kartalla →
            </a>
          </section>
        )}
      </div>
    </div>
  );
};

export default MediaMetadataModal;
